/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import ActionCreator from '../action-creator';
import { FlightPathWaypoint, TerminalDetails } from '../../shared/entities/terminal';
import { PortalsEnum } from '../../shared/entities/portal';

export interface TerminalUpdatePayload {
  terminal: TerminalDetails;
  portal: PortalsEnum;
  useOtherTerminalOnPlatformDataForUpdate?: boolean;
}

export interface SetFlightPathPayload {
  flightPath: FlightPathWaypoint[];
  portal: PortalsEnum;
}

export interface ReceivedFlightPathPayload {
  response: any;
  portal: PortalsEnum;
}

export interface FlightPathErrorPayload {
  error: any;
  portal: PortalsEnum;
}

export interface RequestTailDetailsPanelTerminalPayload {
  datapathUuid: string;
  portal: PortalsEnum;
}

export interface RequestFlightPathPayload {
  datapathUuid: string;
  portal: PortalsEnum;
}

export const ActionCreators = {
  RequestTerminals: new ActionCreator<'RequestTerminals', any[]>('RequestTerminals'),
  SetFlightPath: new ActionCreator<'SetFlightPath', SetFlightPathPayload>('SetFlightPath'),
  RequestTerminalsOnce: new ActionCreator<'RequestTerminalsOnce', any[]>('RequestTerminalsOnce'),
  ReceivedTerminalsSuccess: new ActionCreator<'ReceivedTerminalsSuccess', any>('ReceivedTerminalsSuccess'),
  ReceivedTerminalUpdateSuccess: new ActionCreator<'ReceivedTerminalUpdateSuccess', any>(
    'ReceivedTerminalUpdateSuccess',
  ),
  ReceivedTerminalsError: new ActionCreator<'ReceivedTerminalsError', any>('ReceivedTerminalsError'),
  ClearReceivedTerminalsError: new ActionCreator<'ClearReceivedTerminalsError', any>('ClearReceivedTerminalsError'),
  CancelRequestTerminalsPoll: new ActionCreator<'CancelRequestTerminalsPoll', any[]>('CancelRequestTerminalsPoll'),
  // Terminal Actions for Tail Details Panel
  RequestTailDetailsPanelTerminal: new ActionCreator<
    'RequestTailDetailsPanelTerminal',
    RequestTailDetailsPanelTerminalPayload
  >('RequestTailDetailsPanelTerminal'),
  ReceivedTerminalSuccess: new ActionCreator<'ReceivedTerminalSuccess', TerminalUpdatePayload>(
    'ReceivedTerminalSuccess',
  ),
  ReceivedTailDetailsPanelTerminalError: new ActionCreator<'ReceivedTailDetailsPanelTerminalError', any>(
    'ReceivedTailDetailsPanelTerminalError',
  ),
  ClearReceivedTailDetailsPanelTerminalError: new ActionCreator<'ClearReceivedTailDetailsPanelTerminalError', any>(
    'ClearReceivedTailDetailsPanelTerminalError',
  ),
  CancelRequestTailDetailsPanelTerminal: new ActionCreator<'CancelRequestTailDetailsPanelTerminal', any[]>(
    'CancelRequestTailDetailsPanelTerminal',
  ),
  ClearDetailsPanelTerminal: new ActionCreator<'ClearDetailsPanelTerminal', any>('ClearDetailsPanelTerminal'),
  // Terminal Actions for Requesting Flight Path
  RequestFlightPath: new ActionCreator<'RequestFlightPath', RequestFlightPathPayload>('RequestFlightPath'),
  ClearFlightPath: new ActionCreator<'ClearFlightPath', any>('ClearFlightPath'),
  RequestFlightPathAsync: new ActionCreator<'RequestFlightPathAsync', RequestFlightPathPayload>(
    'RequestFlightPathAsync',
  ),
  ReceivedFlightPathSuccess: new ActionCreator<'ReceivedFlightPathSuccess', ReceivedFlightPathPayload>(
    'ReceivedFlightPathSuccess',
  ),
  ReceivedFlightPathError: new ActionCreator<'ReceivedFlightPathError', FlightPathErrorPayload>(
    'ReceivedFlightPathError',
  ),
  ClearReceivedFlightPathError: new ActionCreator<'ClearReceivedFlightPathError', FlightPathErrorPayload>(
    'ClearReceivedFlightPathError',
  ),
  CancelRequestFlightPath: new ActionCreator<'CancelRequestFlightPath', any>('CancelRequestFlightPath'),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

export function getTerminals() {
  return ActionCreators.RequestTerminals.create();
}

export function getTerminalsOnce() {
  return ActionCreators.RequestTerminalsOnce.create();
}

export function getTailDetailsPanelTerminal(datapathUuid: string, portal = PortalsEnum.UNIFIED_PORTAL) {
  return ActionCreators.RequestTailDetailsPanelTerminal.create({ datapathUuid, portal });
}

export function clearReceivedTailDetailsPanelTerminalError() {
  return (dispatch: any) => {
    dispatch(ActionCreators.ClearReceivedTailDetailsPanelTerminalError.create());
  };
}

export function cancelRequestTerminalsPoll() {
  return ActionCreators.CancelRequestTerminalsPoll.create();
}

export function cancelRequestTailDetailsPanelTerminal() {
  return ActionCreators.CancelRequestTailDetailsPanelTerminal.create();
}

export function clearDetailsPanelTerminal() {
  return ActionCreators.ClearDetailsPanelTerminal.create();
}

export function pollFlightPath(datapathUuid: string, portal = PortalsEnum.UNIFIED_PORTAL) {
  return ActionCreators.RequestFlightPath.create({ datapathUuid, portal });
}

export function cancelRequestFlightPath() {
  return ActionCreators.CancelRequestFlightPath.create();
}

export function clearFlightPath(portal = PortalsEnum.UNIFIED_PORTAL) {
  return ActionCreators.ClearFlightPath.create(portal);
}

export function getFlightPathOnce(datapathUuid: string, portal = PortalsEnum.UNIFIED_PORTAL) {
  return (dispatch: any) => {
    dispatch(ActionCreators.RequestFlightPathAsync.create({ datapathUuid, portal }));
  };
}

export function setFlightPath(flightPath: FlightPathWaypoint[], portal = PortalsEnum.UNIFIED_PORTAL) {
  return (dispatch: any) => {
    dispatch(
      ActionCreators.SetFlightPath.create({
        flightPath,
        portal,
      }),
    );
  };
}

export function syncRequestPollsForTerminal(
  datapathUuid: string,
  syncTerminalsPoll = true,
  syncFlightPathsPoll = true,
  syncTerminalDetailsPoll = true,
  portal = PortalsEnum.UNIFIED_PORTAL,
) {
  return (dispatch: any) => {
    // Restart terminals poll
    if (syncTerminalsPoll) {
      dispatch(ActionCreators.CancelRequestTerminalsPoll.create());
      dispatch(ActionCreators.RequestTerminals.create());
    }

    // Restart flight paths poll
    if (syncFlightPathsPoll) {
      dispatch(ActionCreators.CancelRequestFlightPath.create());
      dispatch(ActionCreators.RequestFlightPath.create({ datapathUuid, portal }));
    }

    // Restart terminal details poll
    if (syncTerminalDetailsPoll) {
      dispatch(ActionCreators.CancelRequestTailDetailsPanelTerminal.create());
      dispatch(ActionCreators.RequestTailDetailsPanelTerminal.create({ datapathUuid, portal }));
    }
  };
}
